// Bespoke access grants — the comped, reviewer, and friend-of-the-house lane
// past the paywall. Deliberately a SEPARATE mechanism from `entitlements`, not
// a flag on it: an entitlement is the record of a real payment (it carries an
// `orderId`, a `receiptJti`, and money that ops can refund), and writing hand-
// issued comps into that table would make "what did we sell?" unanswerable and
// put a hand-editable row in the middle of the purchase audit trail.
//
// KEYED BY THE WORKOS SUBJECT, like entitlements (ADR-3), because no Convex
// store may hold an address (docs/data-architecture/README.md §4) and the
// session token no longer carries one (#88). A person who has no account yet
// is reached by a CLAIM LINK instead: the row holds only SHA-256 of a one-time
// token, the admin sends the link from their own mailbox, and the first
// signed-in account to redeem it becomes the grantee. Whoever holds the link
// can claim it, so it is single use, it expires, and the console shows who
// claimed it — the same trust level as "whoever controls the address", easier
// to forward.

import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import { sha256Hex } from "@bindery/shared/crypto";
import { v, type Infer } from "convex/values";

/**
 * What a grant opens.
 *
 *   `all`    — every book, including ones not yet shipped. The blanket comp.
 *   `book`   — one catalog book, whole: the whole series, every volume of it.
 *   `volume` — one bound volume, expressed as the CHAPTER RANGE it covers
 *              rather than a shelf edition id. `src/reader/shelf.ts` owns the
 *              fact that a book ships as several covers and nothing in the
 *              backend knows it (`convex/model/readMark.ts` makes the same
 *              choice); a chapter range is something both sides already agree
 *              on, and it survives the Odyssey being re-cut into two volumes or
 *              four, where a stored `"odyssey-3"` would quietly mean something
 *              else. `label` is display copy for the console, never matched on.
 */
export const grantScopeValidator = v.union(
  v.object({ kind: v.literal("all") }),
  v.object({ kind: v.literal("book"), bookSlug: v.string() }),
  v.object({
    kind: v.literal("volume"),
    bookSlug: v.string(),
    from: v.number(),
    to: v.number(),
    label: v.string(),
  }),
);
export type GrantScope = Infer<typeof grantScopeValidator>;

/** The chapter a gate is asking about. */
export interface GrantTarget {
  bookSlug: string;
  chapterNumber: number;
}

/** Does this scope open `target`? */
export function grantCovers(scope: GrantScope, target: GrantTarget): boolean {
  switch (scope.kind) {
    case "all":
      return true;
    case "book":
      return scope.bookSlug === target.bookSlug;
    case "volume":
      return (
        scope.bookSlug === target.bookSlug &&
        target.chapterNumber >= scope.from &&
        target.chapterNumber <= scope.to
      );
  }
}

/** Does ANY of these scopes open `target`? The gate's actual question. */
export function anyGrantCovers(
  scopes: readonly GrantScope[],
  target: GrantTarget,
): boolean {
  return scopes.some((scope) => grantCovers(scope, target));
}

/**
 * Are these the same grant, for the purpose of "has this already been issued?"
 *
 * Field-wise rather than a `JSON.stringify` comparison: that would be sensitive
 * to key order, which nothing in a database round trip promises to preserve, and
 * an ordering change would silently turn every re-grant into a duplicate row an
 * admin then has to revoke twice.
 *
 * `label` is deliberately NOT compared. It is display copy for the console, so
 * re-issuing the same chapter range under a renamed volume should UPDATE the
 * row rather than shelve a second one beside it.
 */
export function sameScope(a: GrantScope, b: GrantScope): boolean {
  if (a.kind === "all") return b.kind === "all";
  if (a.kind === "book") return b.kind === "book" && a.bookSlug === b.bookSlug;
  return (
    b.kind === "volume" &&
    a.bookSlug === b.bookSlug &&
    a.from === b.from &&
    a.to === b.to
  );
}

/** One-line description of a scope, for the console and for confirmations. */
export function scopeLabel(scope: GrantScope, bookTitle?: string): string {
  const book = bookTitle ?? ("bookSlug" in scope ? scope.bookSlug : "");
  switch (scope.kind) {
    case "all":
      return "Every book";
    case "book":
      return book;
    case "volume":
      return `${book} · ${scope.label}`;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

export const CLAIM_LINK_TTL_MS = 30 * DAY_MS;

/** §3b promises a review copy that is "temporary, and honest about it". */
export const REVIEW_GRANT_TTL_MS = 90 * DAY_MS;

export function isClaimTokenShaped(raw: string): boolean {
  return UUID_REGEX.test(raw);
}

/** SHA-256 hex. Only the hash is stored, so a read of the table cannot be
 *  replayed as a claim. */
export async function hashClaimToken(token: string): Promise<string> {
  return sha256Hex(token);
}

/**
 * A typo guard for a pasted WorkOS user id, not validation: a grant to an id
 * nobody holds simply never matches. The `user_` prefix is WorkOS's documented
 * one; the alphabet is left loose so a real id is never refused.
 */
export function isWorkosIdShaped(raw: string): boolean {
  return /^user_[0-9A-Za-z]{8,64}$/.test(raw);
}

/** The keying and lifetime fields of a stored grant. */
export interface GrantKeys {
  email?: string;
  workosId?: string;
  claimTokenHash?: string;
  claimExpiresAt?: number;
  claimedAt?: number;
  expiresAt?: number;
}

/** Does this grant still open anything at `now`? */
export function isLive(grant: GrantKeys, now: number): boolean {
  return grant.expiresAt === undefined || now < grant.expiresAt;
}

/** May a signed-in caller redeem this row's link at `now`? */
export function isClaimable(grant: GrantKeys, now: number): boolean {
  return (
    grant.claimTokenHash !== undefined &&
    grant.workosId === undefined &&
    grant.claimExpiresAt !== undefined &&
    now < grant.claimExpiresAt
  );
}

/**
 * Where a grant stands, for the console.
 *
 *   `active`       — held by an account, and not past `expiresAt`.
 *   `expired`      — held by an account, past `expiresAt`.
 *   `link_pending` — a link nobody has claimed yet, still redeemable.
 *   `link_expired` — a link nobody claimed in time.
 *   `legacy`       — keyed only by an address; matches nobody until re-keyed.
 */
export type GrantStatus =
  | "active"
  | "expired"
  | "link_pending"
  | "link_expired"
  | "legacy";

/** Reads no token hash, so the console's listing need not carry one: every
 *  link has a `claimExpiresAt`, and nothing else does. */
export function grantStatus(grant: GrantKeys, now: number): GrantStatus {
  if (grant.workosId !== undefined) {
    return isLive(grant, now) ? "active" : "expired";
  }
  if (grant.claimExpiresAt !== undefined) {
    return now < grant.claimExpiresAt ? "link_pending" : "link_expired";
  }
  return "legacy";
}

/**
 * The expiry a renewed grant should carry: whichever of the two lasts longer,
 * where `undefined` is "never". Re-issuing a review copy over a standing comp
 * must not shorten the comp.
 */
export function laterExpiry(
  a: number | undefined,
  b: number | undefined,
): number | undefined {
  if (a === undefined || b === undefined) return undefined;
  return Math.max(a, b);
}

/**
 * How a stored row is reached at `now`, for the re-key migration's report.
 * A link counts only while it can still be claimed: one that lapsed is
 * `legacy` if its address survives (send it a new link) and `unkeyed`
 * otherwise. `unkeyed` is a grant nobody can ever match or claim: the one
 * state a migration must never leave a row in.
 */
export type GrantKeying = "account" | "link" | "legacy" | "unkeyed";

export function grantKeying(grant: GrantKeys, now: number): GrantKeying {
  if (grant.workosId !== undefined) return "account";
  if (isClaimable(grant, now)) return "link";
  if (grant.email !== undefined) return "legacy";
  return "unkeyed";
}

export type KeyingCounts = Record<GrantKeying, number> & { total: number };

export function countKeyings(
  grants: readonly GrantKeys[],
  now: number,
): KeyingCounts {
  const counts: KeyingCounts = {
    total: grants.length,
    account: 0,
    link: 0,
    legacy: 0,
    unkeyed: 0,
  };
  for (const grant of grants) counts[grantKeying(grant, now)]++;
  return counts;
}
